import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

import {
  InsuranceCountry,
  InsuranceDraft,
  InsuranceParticipantData,
  InsurancePolicyholderData,
  InsurancePolicyholderKind,
  InsuranceProgram,
  InsuranceTripPurpose,
} from './types'

interface InsuranceDraftContextValue {
  draft: InsuranceDraft
  setCountry: (country: InsuranceCountry) => void
  setDates: (startDate: string, endDate: string) => void
  setProgram: (program: InsuranceProgram) => void
  setPurpose: (purpose: InsuranceTripPurpose) => void
  setTravelerAges: (ages: string[]) => void
  setInfantAges: (ages: string[]) => void
  setPolicyholderKind: (kind: InsurancePolicyholderKind) => void
  setPolicyholderData: (
    policyholder: InsurancePolicyholderData,
    participants: InsuranceParticipantData[],
  ) => void
}

const InsuranceDraftContext = createContext<InsuranceDraftContextValue | null>(
  null,
)

interface InsuranceDraftProviderProps {
  children: ReactNode
}

export const InsuranceDraftProvider: FC<InsuranceDraftProviderProps> = ({
  children,
}) => {
  const [draft, setDraft] = useState<InsuranceDraft>({
    country: null,
    program: null,
    purpose: null,
    policyholderKind: null,
    policyholder: null,
    participants: [],
    travelerAges: [''],
    infantAges: [],
  })

  const setCountry = useCallback((country: InsuranceCountry) => {
    setDraft((current) =>
      current.country?.id === country.id
        ? current
        : {
            ...current,
            country,
            program: null,
          },
    )
  }, [])

  const setDates = useCallback((startDate: string, endDate: string) => {
    setDraft((current) => ({
      ...current,
      startDate,
      endDate,
    }))
  }, [])

  const setProgram = useCallback((program: InsuranceProgram) => {
    setDraft((current) => ({
      ...current,
      program,
    }))
  }, [])

  const setPurpose = useCallback((purpose: InsuranceTripPurpose) => {
    setDraft((current) => ({
      ...current,
      purpose,
    }))
  }, [])

  const setTravelerAges = useCallback((travelerAges: string[]) => {
    setDraft((current) => ({ ...current, travelerAges }))
  }, [])

  const setInfantAges = useCallback((infantAges: string[]) => {
    setDraft((current) => ({ ...current, infantAges }))
  }, [])

  const setPolicyholderKind = useCallback((kind: InsurancePolicyholderKind) => {
    setDraft((current) => ({
      ...current,
      policyholderKind: kind,
      policyholder: null,
    }))
  }, [])

  const setPolicyholderData = useCallback(
    (
      policyholder: InsurancePolicyholderData,
      participants: InsuranceParticipantData[],
    ) => {
      setDraft((current) => ({
        ...current,
        policyholder,
        participants,
      }))
    },
    [],
  )

  const value = useMemo(
    () => ({
      draft,
      setCountry,
      setDates,
      setProgram,
      setPurpose,
      setTravelerAges,
      setInfantAges,
      setPolicyholderKind,
      setPolicyholderData,
    }),
    [
      draft,
      setCountry,
      setDates,
      setPolicyholderData,
      setPolicyholderKind,
      setProgram,
      setPurpose,
      setTravelerAges,
      setInfantAges,
    ],
  )

  return (
    <InsuranceDraftContext.Provider value={value}>
      {children}
    </InsuranceDraftContext.Provider>
  )
}

export const useInsuranceDraft = () => {
  const context = useContext(InsuranceDraftContext)

  if (!context) {
    throw new Error(
      'useInsuranceDraft must be used inside InsuranceDraftProvider',
    )
  }

  return context
}
